// deps
import React, { ReactElement, useMemo, memo } from "react";

// internal-deps
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";

// interfaces
import {
    CardPaymentMethodData,
    IWalletPaymentGroup,
    NetBankingPaymentGroupActions,
    NetBankingPaymentMethodData,
    PayLaterPaymentGroupActions,
    PayLaterPaymentMethodData,
    WalletPaymentGroupActions,
    WalletPaymentMethodData,
} from "@payment-ui/interfaces";
import { PreferredRendererProps } from "@payment-ui/components/payment-group/preferred/interfaces";

// components
import Card from "./../other-payment-offers-methods/NewCard/NewCardOffers";
import NetBanking from "./../other-payment-offers-methods/NetBanking/NetBankingOffers";
import PayLater from "./../other-payment-offers-methods/PayLater/PayLaterOffers";
import Wallet from "./../other-payment-offers-methods/Wallet/NewWalletOffers";

interface OtherPaymentOffersRendererProps extends PreferredRendererProps {
    walletGroupData?: IWalletPaymentGroup["paymentData"];
}

function OtherPaymentOffersMethodRenderer(props: OtherPaymentOffersRendererProps): ReactElement | null {
    const { paymentAmount, user, methodData, getPaymentActions, getPaymentMethodProps } = props;
    const groupName = methodData.groupName.toUpperCase();
    const methodProps = useMemo(() => getPaymentMethodProps(methodData), [getPaymentMethodProps, methodData]);
    const paymentMethodActions = useMemo(() => getPaymentActions(groupName), [getPaymentActions, groupName]);

    switch (groupName) {
        case VALID_PAYMENT_GROUP_TYPES.CARD:
        case VALID_PAYMENT_GROUP_TYPES.FOOD_CARD:
            return (
                <Card
                    {...methodProps}
                    paymentActions={paymentMethodActions}
                    methodData={methodData as CardPaymentMethodData}
                />
            );

        case VALID_PAYMENT_GROUP_TYPES.WALLET:
        case VALID_PAYMENT_GROUP_TYPES.WALLET_WEB:
            return (
                <Wallet
                    {...methodProps}
                    user={user}
                    paymentActions={paymentMethodActions as WalletPaymentGroupActions}
                    methodData={methodData as WalletPaymentMethodData}
                    paymentAmount={paymentAmount}
                    paymentGroup={props.walletGroupData}
                />
            );

        case VALID_PAYMENT_GROUP_TYPES.NETBANKING:
            return (
                <NetBanking
                    {...methodProps}
                    paymentActions={paymentMethodActions as NetBankingPaymentGroupActions}
                    methodData={methodData as NetBankingPaymentMethodData}
                    paymentAmount={paymentAmount}
                    isPreferredMethod
                />
            );

        case VALID_PAYMENT_GROUP_TYPES.PAY_LATER:
            return user != null ? (
                <PayLater
                    {...methodProps}
                    user={user}
                    paymentActions={paymentMethodActions as PayLaterPaymentGroupActions}
                    methodData={methodData as PayLaterPaymentMethodData}
                />
            ) : null;

        default:
            return null;
    }
}

export default memo(OtherPaymentOffersMethodRenderer);
